import axiosInstance from "../../utility/axios";

import {
  deleteService,
  setLoading,
  createService,
  setServices,
  setTotal,
  editService,
  setService,
  setLoadingServiceId,
} from "../../store/slices/services";
import { AppDispatch } from "../../store/store";
import {
  ICreateService,
  IEditService,
  IService,
  IServiceAnalyticData,
  IServiceResponseData,
} from "../../store/types/service";
import { toast } from "sonner";
import { IApiResponse } from "@/src/store/types/api-response";
import { getErrorMessage } from "../apiError";

const serviceEndpoint = "/product/api/v1/services";

export const get_services = async (
  dispatch: AppDispatch,
  limit: number,
  offset: number,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get<
      IApiResponse<IServiceResponseData>
    >(`${serviceEndpoint}`, { params: { limit, offset } });
    dispatch(setServices(response.data));
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load services"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const get_service = async (
  dispatch: AppDispatch,
  id: string | number,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get<IApiResponse<IService>>(
      `${serviceEndpoint}/${id}`,
    );

    dispatch(setService(response.data.data));
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load service"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const create_service = async (
  form: ICreateService,
  dispatch: AppDispatch,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post<IApiResponse<IService>>(
      serviceEndpoint,
      form,
    );
    const { message, data } = response.data;
    toast.success(message || "Service created successfully");
    dispatch(createService(data));
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to create service"));
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const edit_service = async (
  form: IEditService,
  dispatch: AppDispatch,
) => {
  dispatch(setLoading(true));
  try {
    const { id, ...payload } = form;
    const response = await axiosInstance.patch<IApiResponse<IService>>(
      `${serviceEndpoint}/${id}`,
      payload,
    );

    const { data, message } = response.data;
    toast.success(message || "Service updated successfully");
    dispatch(editService(data));
    dispatch(setService(data));
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update service"));
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const delete_service = async (dispatch: AppDispatch, id: number) => {
  dispatch(setLoadingServiceId(id));
  try {
    const response = await axiosInstance.delete<IApiResponse<null>>(
      `${serviceEndpoint}/${id}`,
    );
    const { message } = response.data;
    dispatch(deleteService(Number(id)));
    toast.success(message || "Service deleted successfully");
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to delete service"));
  } finally {
    dispatch(setLoadingServiceId(null));
  }
};

// ================================================================================

export const get_total_service = async (
  dispatch: AppDispatch,
): Promise<void> => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get<
      IApiResponse<IServiceAnalyticData>
    >(`${serviceEndpoint}/analytics`);
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load service total");
    }
    dispatch(setTotal(response.data.data));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load service total"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const update_service_status = async (
  dispatch: AppDispatch,
  id: string | number,
) => {
  try {
    const response = await axiosInstance.patch<IApiResponse<IService>>(
      `${serviceEndpoint}/${id}/status`,
    );

    const { data, message } = response.data;
    dispatch(editService(data));
    toast.success(message || "Service status updated successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update service status"));
    return false;
  }
};

export const delete_package = async (dispatch: AppDispatch, id: number) => {
  dispatch(setLoadingServiceId(id));
  try {
    const response = await axiosInstance.delete<IApiResponse<IService>>(
      `${serviceEndpoint}/${id}/packages`,
    );

    const { message, data } = response.data;
    dispatch(editService(data));
    dispatch(setService(data));
    toast.success(message || "Package deleted successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to delete package"));
    return false;
  } finally {
    dispatch(setLoadingServiceId(null));
  }
};
